import { useCallback, useEffect, useMemo, useState, FormEvent } from "react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { fetchLeads, type CrmLead } from "@/api/crm";

type LeadSearchModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (lead: CrmLead) => void;
  initialQuery?: string;
};

export function LeadSearchModal({ open, onClose, onSelect, initialQuery }: LeadSearchModalProps) {
  const [query, setQuery] = useState(initialQuery ?? "");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CrmLead[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery ?? "");
    setResults([]);
    setError(null);
  }, [open, initialQuery]);

  const handleSearch = useCallback(
    async (event?: FormEvent) => {
      if (event) event.preventDefault();
      setLoading(true);
      setError(null);
      try {
        const leads = await fetchLeads({});
        const trimmed = query.trim().toLowerCase();
        const filtered = trimmed
          ? leads.filter((lead) => {
              const title = (lead.title || "").toLowerCase();
              const region = (lead.region || "").toLowerCase();
              const stage = (lead.pipeline_stage || "").toLowerCase();
              return (
                title.includes(trimmed) ||
                region.includes(trimmed) ||
                stage.includes(trimmed)
              );
            })
          : leads;
        setResults(filtered);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to search leads.";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [query],
  );

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void handleSearch();
    }, 200);
    return () => window.clearTimeout(timer);
  }, [open, query, handleSearch]);

  const hasResults = useMemo(() => results.length > 0, [results.length]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Link to existing lead</DialogTitle>
          <DialogDescription>
            Search your open CRM leads by title, region, or stage and attach this email thread to the right record.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col gap-3">
          <form onSubmit={handleSearch} className="space-y-3">
            <Input
              autoFocus
              placeholder="Search leads (title, region, stage)…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </form>
          {error && <p className="text-xs text-rose-300">{error}</p>}
          <div className="min-h-[6rem] flex-1 space-y-2 overflow-y-auto rounded-xl border border-border bg-background/70 p-2 text-sm text-foreground">
            {loading && <p className="text-xs text-muted-foreground">Searching open leads…</p>}
            {!loading && !hasResults && (
              <p className="text-xs text-muted-foreground">
                {query.trim() ? "No leads match this search." : "Start typing to search your leads."}
              </p>
            )}
            {!loading &&
              hasResults &&
              results.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => {
                    onSelect(lead);
                    onClose();
                  }}
                  className="flex w-full flex-col items-start rounded-xl border border-border bg-background/40 px-3 py-2 text-left text-xs hover:border-amber-400/70 hover:bg-background/40"
                >
                  <span className="text-sm font-semibold text-foreground">{lead.title || `Lead ${lead.id}`}</span>
                  <span className="text-[11px] text-muted-foreground">
                    Stage: {lead.pipeline_stage} · Region: {lead.region || "—"}
                  </span>
                </button>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
